/**
 * PMGT FE-2 — the two fully-shared step bodies for the unified wizard:
 * **Basics** (name / offer ref / report language / dates / AI models) and
 * **Parties** (customer / contractor / reporting). Both modes render these
 * identically; the lifted state + handlers are passed in by the orchestrator.
 */
import { type ReactElement, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AiModelMultiSelect } from "../AiModelMultiSelect.js";
import type { KnownTenant } from "../../tenancy/types.js";
import {
  ContractPartyField,
  CreatePartyField,
  PARTY_INFO,
  PartyHeadline,
  PrefilledPartyField,
  REPORT_LANGUAGES,
  ReportingPartyField,
  type ContractPartyValue,
  type PartyValue,
  type ReportingValue,
  type WizardErrors,
} from "./wizardParts.js";
import {
  SET_BY_WORKFLOW,
  TODAY_RULES,
  isHidden,
  isPrefilled,
  isRequired,
  partyRules,
  type FieldRule,
  type FieldRules,
} from "./fieldRules.js";
import { PartiesHelpDialog } from "./PartiesHelpDialog.js";
import { CircleHelpIcon, InfoIcon } from "@aeriosiq/ui/icons";

/** FB-21 — a small info affordance for a Basics field label. Tooltip-wrapped
 *  InfoOutlined icon button; `aria-label` carries the field name so a11y +
 *  tests can target it by accessible name. Used for the date "Project period"
 *  group label (the date pickers host their own native indicator, so the icon
 *  stays beside the group label rather than in-field — FB-22). */
function FieldInfoIcon({
  label,
  text,
}: {
  label: string;
  text: string;
}): ReactElement {
  return (
    <Tooltip title={text}>
      <IconButton size="small" aria-label={label} sx={{ p: 0.25 }}>
        <InfoIcon fontSize="small" color="action" />
      </IconButton>
    </Tooltip>
  );
}

/** FB-22 — the SAME info affordance, but rendered INSIDE a TextField as an end
 *  `InputAdornment`, mirroring the scope-filter widget on the Turbine Models
 *  page (`ScopeFilterWithInfo` → `SingleSelectFilter` `endAdornment`). The
 *  `IconButton` keeps its `aria-label` (a11y + existing test selectors) and the
 *  same Tooltip copy. `onMouseDown` stops propagation so clicking the icon
 *  doesn't steal focus interactions from the field (matches the Turbine Models
 *  pattern). Spread into `slotProps.input.endAdornment`. */
function FieldInfoAdornment({
  label,
  text,
}: {
  label: string;
  text: string;
}): ReactElement {
  return (
    <InputAdornment position="end">
      <Tooltip title={text}>
        <IconButton
          size="small"
          edge="end"
          aria-label={label}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          <InfoIcon fontSize="small" color="action" />
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );
}

const OFFER_REFERENCE_INFO =
  "Reference to the official offer — e.g. an offer number or the customer's internal number.";
const DATE_RANGE_INFO =
  "The planned period for the project — informational only; it does not affect project execution or block anything.";

export interface BasicsStepProps {
  projectName: string;
  onProjectName: (v: string) => void;
  offerReference: string;
  onOfferReference: (v: string) => void;
  reportLanguage: string;
  onReportLanguage: (v: string) => void;
  startDate: string;
  onStartDate: (v: string) => void;
  endDate: string;
  onEndDate: (v: string) => void;
  aiModelIds: string[];
  onAiModelIds: (ids: string[]) => void;
  maxAiModels: number | null;
  showErrors: boolean;
  errors: WizardErrors;
  /** Create mode renders the inline AI-model multi-select inside Basics. Edit
   *  mode hides it (the dedicated `AiModelsPanel`, with its own PUT, owns the
   *  selection there). Defaults true. */
  showAiModels?: boolean;
  /** M3C-04 — the picked version's field rules (absent = every field as today). */
  rules?: FieldRules;
}

/** M3C-04 — a Basics field the workflow prefills: read-only, "set by the workflow". */
function PrefilledText({
  label,
  rule,
}: {
  label: string;
  rule: FieldRule;
}): ReactElement {
  return (
    <TextField
      label={label}
      value={rule.value ?? ""}
      helperText={SET_BY_WORKFLOW}
      slotProps={{ htmlInput: { readOnly: true, "aria-readonly": true } }}
      sx={{ flex: 1 }}
    />
  );
}

export function BasicsStep({
  projectName,
  onProjectName,
  offerReference,
  onOfferReference,
  reportLanguage,
  onReportLanguage,
  startDate,
  onStartDate,
  endDate,
  onEndDate,
  aiModelIds,
  onAiModelIds,
  maxAiModels,
  showErrors,
  errors,
  showAiModels = true,
  rules = TODAY_RULES,
}: BasicsStepProps): ReactElement {
  const fieldError = (f: keyof NonNullable<WizardErrors["fields"]>) =>
    showErrors ? errors.fields?.[f] : undefined;
  const showDates = !isHidden(rules.startDate) || !isHidden(rules.endDate);
  const showModels = showAiModels && !isHidden(rules.aiModelIds);
  return (
    <Box component="section" aria-label="Project basics">
      <Typography variant="h6" gutterBottom>
        Project
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Project name"
          required
          error={showErrors && errors.projectName !== undefined}
          helperText={showErrors ? errors.projectName : undefined}
          value={projectName}
          onChange={(e) => {
            onProjectName(e.target.value);
          }}
          slotProps={{ htmlInput: { "aria-label": "Project name" } }}
          sx={{ maxWidth: 480 }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {isHidden(rules.offerReference) ? null : isPrefilled(
              rules.offerReference,
            ) ? (
            <PrefilledText
              label="Offer reference"
              rule={rules.offerReference}
            />
          ) : (
            <TextField
              label="Offer reference"
              value={offerReference}
              required={isRequired(rules.offerReference)}
              error={fieldError("offerReference") !== undefined}
              helperText={fieldError("offerReference")}
              onChange={(e) => {
                onOfferReference(e.target.value);
              }}
              sx={{ flex: 1 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <FieldInfoAdornment
                      label="About the Offer reference field"
                      text={OFFER_REFERENCE_INFO}
                    />
                  ),
                },
              }}
            />
          )}
          {isHidden(rules.reportLanguage) ? null : isPrefilled(
              rules.reportLanguage,
            ) ? (
            <PrefilledText
              label="Report language"
              rule={rules.reportLanguage}
            />
          ) : (
            <TextField
              select
              label="Report language"
              value={reportLanguage}
              required={isRequired(rules.reportLanguage)}
              error={fieldError("reportLanguage") !== undefined}
              helperText={fieldError("reportLanguage")}
              onChange={(e) => {
                onReportLanguage(e.target.value);
              }}
              sx={{ flex: 1 }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {REPORT_LANGUAGES.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        {showDates ? (
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="subtitle2">Project period</Typography>
              <FieldInfoIcon
                label="About the project period (start and end date)"
                text={DATE_RANGE_INFO}
              />
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 1 }}
            >
              {isHidden(rules.startDate) ? null : isPrefilled(
                  rules.startDate,
                ) ? (
                <PrefilledText label="Start date" rule={rules.startDate} />
              ) : (
                <TextField
                  type="date"
                  label="Start date"
                  value={startDate}
                  required={isRequired(rules.startDate)}
                  error={fieldError("startDate") !== undefined}
                  helperText={fieldError("startDate")}
                  onChange={(e) => {
                    onStartDate(e.target.value);
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ flex: 1 }}
                />
              )}
              {isHidden(rules.endDate) ? null : isPrefilled(rules.endDate) ? (
                <PrefilledText label="End date" rule={rules.endDate} />
              ) : (
                <TextField
                  type="date"
                  label="End date"
                  value={endDate}
                  required={isRequired(rules.endDate)}
                  error={fieldError("endDate") !== undefined}
                  helperText={fieldError("endDate")}
                  onChange={(e) => {
                    onEndDate(e.target.value);
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ flex: 1 }}
                />
              )}
            </Stack>
          </Box>
        ) : null}
        {fieldError("aiModelIds") ? (
          <Typography variant="caption" color="error">
            {fieldError("aiModelIds")}
          </Typography>
        ) : null}
        {showModels ? (
          <AiModelMultiSelect
            value={aiModelIds}
            onChange={onAiModelIds}
            maxSelections={maxAiModels}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

export interface PartiesStepProps {
  customer: PartyValue;
  onCustomer: (v: PartyValue) => void;
  contractor: PartyValue;
  onContractor: (v: PartyValue) => void;
  reporting: ReportingValue;
  onReporting: (v: ReportingValue) => void;
  knownTenants: readonly KnownTenant[];
  knownTenantsLoading: boolean;
  showErrors: boolean;
  errors: WizardErrors;
  /** FB-18/FB-44 — project-wide contract-party DEFAULTS (each park inherits +
   *  overrides). Rendered for everyone who can edit a project, like the project
   *  parties; binding a contract party is enforced on the BE (PM-D23). */
  contractContractorDefault: ContractPartyValue;
  onContractContractorDefault: (next: ContractPartyValue) => void;
  contractCustomerDefault: ContractPartyValue;
  onContractCustomerDefault: (next: ContractPartyValue) => void;
  /** M3C-04 — the picked version's field rules (absent = every field as today). */
  rules?: FieldRules;
}

export function PartiesStep({
  customer,
  onCustomer,
  contractor,
  onContractor,
  reporting,
  onReporting,
  knownTenants,
  knownTenantsLoading,
  showErrors,
  errors,
  contractContractorDefault,
  onContractContractorDefault,
  contractCustomerDefault,
  onContractCustomerDefault,
  rules = TODAY_RULES,
}: PartiesStepProps): ReactElement {
  const [helpOpen, setHelpOpen] = useState(false);
  const customerRules = partyRules(rules, "customer");
  const contractorRules = partyRules(rules, "contractor");
  const reportingRule = rules.reportingSubcontractorTenantId;

  return (
    <Box component="section" aria-label="Project parties">
      {/* FB-20 — step header + general Help button → who-sees-what modal. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="h6">Parties</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<CircleHelpIcon fontSize="small" />}
          onClick={() => {
            setHelpOpen(true);
          }}
        >
          Who sees what?
        </Button>
      </Stack>

      {/* FB-20 — fixed party order: (1) Project Customer, (2) Project
          Contractor, (3) Contract Customer, (4) Contract Contractor,
          (5) Reporting Subcontractor. FB-44 — the contract-scoped pair (3 & 4)
          renders for everyone who can edit a project, exactly like the project
          parties; tenant-binding stays the BE security boundary (a non-admin
          tenant-bind 403s, PM-D23). The order no longer collapses for
          non-admins. */}
      <Stack spacing={3}>
        {customerRules.hidden ? null : customerRules.prefilled ? (
          <PrefilledPartyField
            title="Project Customer"
            value={customerRules.prefilled.value}
            knownTenants={knownTenants}
            testId="prefilled-customer"
          />
        ) : (
          <CreatePartyField
            kind="customer"
            value={customer}
            onChange={onCustomer}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsLoading}
            error={showErrors ? (errors.customer ?? null) : null}
            modes={customerRules.modes}
            required={customerRules.required}
          />
        )}
        {contractorRules.hidden ? null : contractorRules.prefilled ? (
          <PrefilledPartyField
            title="Project Contractor"
            value={contractorRules.prefilled.value}
            knownTenants={knownTenants}
            testId="prefilled-contractor"
          />
        ) : (
          <CreatePartyField
            kind="contractor"
            value={contractor}
            onChange={onContractor}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsLoading}
            error={showErrors ? (errors.contractor ?? null) : null}
            modes={contractorRules.modes}
            required={contractorRules.required}
          />
        )}

        {/* FB-18/FB-44 — contract-scoped parties (project-wide defaults; each
            park inherits + can override on step 3). FB-20 — each picker carries
            its own headline + info icon. Rendered for everyone who can edit the
            project, like the project parties above; the BE 403s a non-admin
            tenant-bind (PM-D23). */}
        <Box
          component="section"
          aria-label="Contract Customer defaults"
          sx={{ display: "flex", flexDirection: "column", gap: 1 }}
        >
          <PartyHeadline
            title="Contract Customer"
            infoLabel="About the Contract Customer party"
            infoText={PARTY_INFO.contractCustomer}
          />
          <Typography variant="caption" color="text.secondary">
            Optional default applied to every park&apos;s contract — you can
            override it per park.
          </Typography>
          <ContractPartyField
            kind="contractCustomer"
            label="Contract customer (default)"
            value={contractCustomerDefault}
            onChange={onContractCustomerDefault}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsLoading}
          />
        </Box>
        <Box
          component="section"
          aria-label="Contract Contractor defaults"
          sx={{ display: "flex", flexDirection: "column", gap: 1 }}
        >
          <PartyHeadline
            title="Contract Contractor"
            infoLabel="About the Contract Contractor party"
            infoText={PARTY_INFO.contractContractor}
          />
          <Typography variant="caption" color="text.secondary">
            Optional default applied to every park&apos;s contract — you can
            override it per park.
          </Typography>
          <ContractPartyField
            kind="contractContractor"
            label="Contract contractor (default for all parks)"
            value={contractContractorDefault}
            onChange={onContractContractorDefault}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsLoading}
          />
        </Box>

        {isHidden(reportingRule) ? null : isPrefilled(reportingRule) ? (
          <PrefilledPartyField
            title="Reporting Subcontractor"
            value={reportingRule.value}
            knownTenants={knownTenants}
            testId="prefilled-reporting"
          />
        ) : (
          <ReportingPartyField
            value={reporting}
            onChange={onReporting}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsLoading}
            error={showErrors ? (errors.reporting ?? null) : null}
          />
        )}
      </Stack>

      <PartiesHelpDialog
        open={helpOpen}
        onClose={() => {
          setHelpOpen(false);
        }}
      />
    </Box>
  );
}
